/* PDP-2026 (T18): media-type toggle — switches between the gallery and the 3D
   model. The "more media" button is gone: video and the curated custom.gallery
   images are ordinary slides in <pdp-gallery> now, so there is nothing to
   toggle to. All lookups null-guarded (custom elements can upgrade before late
   DOM). */
if (!customElements.get("pdp-gallery-toggle")) {
  class PdpGalleryToggle extends HTMLElement {
    connectedCallback() {
      const wrap = this.closest(".pdp-gallery-wrap");
      const gallery = wrap ? wrap.querySelector("pdp-gallery") : null;
      if (!gallery) return;

      const buttons = this.querySelectorAll("button[data-media-type]");
      const section = this.closest(".nays-pdp-2026");
      const modelWrapper = section
        ? section.querySelector(".product-model__wrapper")
        : null;
      const mediaView = wrap.querySelector(".pdp-media-view");
      const desktop = window.matchMedia(
        "(min-width: 1025px), (min-width: 769px) and (orientation: landscape)",
      );
      const setActive = (type) =>
        buttons.forEach((b) =>
          b.classList.toggle("active", b.dataset.mediaType === type),
        );

      buttons.forEach((btn) => {
        btn.addEventListener("click", () => {
          const type = btn.dataset.mediaType;
          /* "media" opens the mobile sheet rather than switching the gallery, so
             it does not take the active state — the gallery is still showing.
             Restored 2026-08-10 with the mobile drawer; the rebuild had removed
             this branch along with the drawer itself. */
          /* Desktop takes over the gallery area, exactly like the 3D view;
             mobile opens the bottom sheet and keeps the gallery showing, so it
             does not take the active state. */
          if (type === "media" && !desktop.matches) {
            const sheet = document.getElementById("pdp-media-sheet");
            if (sheet && typeof sheet.open === "function") sheet.open();
            return;
          }
          if (mediaView) mediaView.classList.toggle("active", type === "media");
          if (modelWrapper) {
            modelWrapper.classList.toggle("active", type === "model");
            /* model-viewer.js (284 KB) is deferred until someone actually opens
               the 3D view; the markup is already in the DOM, the script just
               upgrades it. */
            if (type === "model" && modelWrapper.dataset.modelViewerSrc) {
              const s = document.createElement("script");
              s.type = "module";
              s.src = modelWrapper.dataset.modelViewerSrc;
              document.head.appendChild(s);
              delete modelWrapper.dataset.modelViewerSrc;

              /* Flag the element once the GLB is actually on screen. CSS keeps
                 the slotted "View in AR" button hidden until then: the custom
                 element upgrades within ~250ms but the model takes seconds, and
                 model-viewer shows its ar-button slot from upgrade onward — so
                 without this the button floats over an empty box for the whole
                 download. `loaded` is already true if we somehow miss the
                 event. */
              const mv = modelWrapper.querySelector("model-viewer");
              if (mv) {
                const flag = () => (mv.dataset.modelLoaded = "");
                if (mv.loaded) flag();
                else mv.addEventListener("load", flag, { once: true });
              }
            }
          }
          setActive(type);
        });
      });

      /* A thumbnail click means "back to the images" as well as "show that one":
         the rail is reachable over both takeovers (it slides back out on hover
         while the media panel is open), so clicking one has to close whichever
         view is covering the gallery.

         Guarded on either overlay, not on modelWrapper alone — products with
         extra media but no 3D model have a media panel and no way out of it
         from the rail. */
      if (modelWrapper || mediaView) {
        gallery.addEventListener(
          "click",
          (e) => {
            if (!e.target.closest("[data-gal-thumb], [data-gal-dot]")) return;
            if (modelWrapper) modelWrapper.classList.remove("active");
            if (mediaView) mediaView.classList.remove("active");
            setActive("image");
          },
          true,
        );
      }
    }
  }
  customElements.define("pdp-gallery-toggle", PdpGalleryToggle);
}

/* T18b: the shared tab snippet hard-opens index 0 — design wants all accordion
   rows closed initially (Übersicht is no longer a tab). Defer script = DOM parsed. */
document
  .querySelectorAll(".nays-pdp-2026 #vertical-tabnavigation .active")
  .forEach((el) => el.classList.remove("active"));

/* T25: magnifier hidden per design — clicking the image opens the
   fullscreen zoom by forwarding to the per-media product-image-zoom. */
document
  .querySelectorAll(".nays-pdp-2026 .pdp-gallery-wrap css-slider")
  .forEach((slider) => {
    slider.addEventListener("click", (e) => {
      const item = e.target.closest(
        '.product-gallery-item[data-product-media-type="image"]',
      );
      if (!item || e.target.closest("product-image-zoom")) return;
      const zoomEl = item.querySelector("product-image-zoom");
      if (zoomEl) zoomEl.click();
    });
  });

/* T26: reco slider chevrons (document-level delegation — complementary
   content is injected async by product-recommendations). */
document.addEventListener("click", (e) => {
  const btn = e.target.closest(
    ".nays-pdp-2026 [data-reco-prev], .nays-pdp-2026 [data-reco-next]",
  );
  if (!btn) return;
  const reco = btn.closest(".pdp-reco");
  const track = reco ? reco.querySelector("[data-reco-track]") : null;
  if (!track) return;
  const dir = btn.hasAttribute("data-reco-next") ? 1 : -1;
  track.scrollBy({ left: dir * track.clientWidth, behavior: "smooth" });
});

/* Chevrons only when the track actually overflows. Recommendations are injected
   async by product-recommendations, so re-check on mutation as well as resize. */
const syncRecoNavs = () => {
  document.querySelectorAll(".nays-pdp-2026 .pdp-reco").forEach((reco) => {
    const track = reco.querySelector("[data-reco-track]");
    const nav = reco.querySelector(".pdp-reco__nav");
    if (!track || !nav) return;
    nav.hidden = track.scrollWidth - track.clientWidth < 2;
  });
};
syncRecoNavs();
window.addEventListener("resize", syncRecoNavs);
document.querySelectorAll(".nays-pdp-2026").forEach((root) =>
  new MutationObserver(syncRecoNavs).observe(root, {
    childList: true,
    subtree: true,
  }),
);

/* Size options are a single horizontal scroll row, so later sizes sit off-screen.
   Centre the chosen pill in the row on selection. Scrolls the row itself rather
   than using scrollIntoView, which would also move the page vertically. */
document.addEventListener("change", (e) => {
  const input = e.target.closest && e.target.closest(".product-variant__input");
  if (!input) return;
  const item = input.closest(".product-variant__item");
  const row = input.closest(
    '.product-variant[data-name*="product-size"] .product-variant__container',
  );
  if (!row || !item || row.scrollWidth <= row.clientWidth) return;
  row.scrollTo({
    left: item.offsetLeft - (row.clientWidth - item.offsetWidth) / 2,
    behavior: "smooth",
  });
});

/* T26: video facade — inject the YouTube iframe only on click */
document.addEventListener("click", (e) => {
  const facade = e.target.closest(".nays-pdp-2026 .pdp-video__facade");
  if (!facade || !facade.dataset.videoEmbed) return;
  const url = facade.dataset.videoEmbed;
  const glue = url.includes("?") ? "&" : "?";
  const iframe = document.createElement("iframe");
  iframe.className = "pdp-video__iframe";
  iframe.src =
    url +
    glue +
    "autoplay=1&modestbranding=1&color=white&controls=1&playsinline=1&rel=0";
  iframe.setAttribute(
    "allow",
    "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share",
  );
  iframe.setAttribute("allowfullscreen", "");
  facade.replaceWith(iframe);
});

/* promo banner: copy the discount code, swap copy icon -> check for 2s */
document.addEventListener("click", (e) => {
  const btn = e.target.closest(".nays-pdp-2026 .nays-pdp-2026__promo-code");
  if (!btn) return;
  navigator.clipboard.writeText(btn.dataset.code).then(() => {
    btn.classList.add("is-copied");
    setTimeout(() => btn.classList.remove("is-copied"), 2000);
  });
});

/* WhatsApp consult: unhide inside the schedule (Europe/Berlin, end hour
   exclusive). Blank days or hours = never. Single gate for future rules. */
function consultOpen(el) {
  const { days, start, end } = el.dataset;
  if (!days || start === "" || end === "") return false;
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "Europe/Berlin",
    weekday: "short",
    hour: "numeric",
    hourCycle: "h23",
  }).formatToParts();
  const get = (type) => parts.find((p) => p.type === type).value;
  const hour = Number(get("hour"));
  return (
    days.split(" ").includes(get("weekday").toLowerCase()) &&
    hour >= Number(start) &&
    hour < Number(end)
  );
}
document.querySelectorAll(".nays-pdp-2026__consult").forEach((el) => {
  if (consultOpen(el)) el.hidden = false;
});

/* T31: reco click analytics. NOTE: the original theme script was dead code —
   it listened for "recommendations:loaded" (the element dispatches
   "product-recommendations-loaded") and called an undefined trackEvent().
   This pushes to dataLayer (GA4/GTM) and still honors window.trackEvent
   if an app ever defines it. */
document.addEventListener("click", (e) => {
  const el = e.target.closest(".nays-pdp-2026 [data-analytics-payload]");
  if (!el) return;
  try {
    const payload = JSON.parse(el.dataset.analyticsPayload);
    if (typeof window.trackEvent === "function") {
      window.trackEvent(payload.type, payload);
    }
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: payload.type, ...payload });
  } catch (err) {
    /* malformed payload — ignore */
  }
});

/* T34: mobile sticky ATC bar (2540:2689) — mirrors the real button's label
   and state; shows once the inline cart controls leave the viewport. */
(() => {
  const bar = document.querySelector("[data-pdp-sticky-bar]");
  const cart = document.querySelector(".nays-pdp-2026 .cart-functions");
  const realAtc = document.querySelector(
    ".nays-pdp-2026 .product__add-to-cart",
  );
  const proxy = bar ? bar.querySelector("[data-pdp-sticky-atc]") : null;
  if (!bar || !cart || !realAtc || !proxy) return;

  /* label and variant are separate spans inside the button — write to the label
     span, never the button, or the variant span gets wiped */
  const atcLabel = proxy.querySelector("[data-pdp-sticky-atc-label]");
  const sync = () => {
    if (atcLabel) atcLabel.textContent = realAtc.textContent.trim();
    proxy.disabled = realAtc.disabled || realAtc.classList.contains("disabled");
    proxy.classList.toggle("disabled", proxy.disabled);
  };
  sync();

  proxy.addEventListener("click", () => realAtc.click());

  /* variant switches update the page without reload — mirror them */
  const label = bar.querySelector("[data-pdp-sticky-variant]");
  const updateLabel = () => {
    const values = Array.from(
      document.querySelectorAll(".product-variant__input:checked"),
    ).map((i) => i.value);
    if (values.length && label) label.textContent = values.join(" / ");
  };
  document.addEventListener("change", (e) => {
    if (e.target.closest && e.target.closest("product-variants")) {
      updateLabel();
      setTimeout(sync, 100); // theme JS updates the real button async
    }
  });
  new MutationObserver(sync).observe(realAtc, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ["class", "disabled"],
  });

  new IntersectionObserver(
    (entries) => {
      const entry = entries[0];
      /* Only once the buy button has scrolled off the TOP. Testing isIntersecting
         alone also matched "still below the fold", so the bar appeared on load
         before the shopper had reached the real button. boundingClientRect.top < 0
         means it is above the viewport, i.e. genuinely scrolled past. */
      const scrolledPast =
        !entry.isIntersecting && entry.boundingClientRect.top < 0;
      bar.hidden = !scrolledPast;
      if (!bar.hidden) sync();
    },
    { rootMargin: "0px" },
  ).observe(cart);
})();

/* Media panels ("Weitere Medien"). Content comes from custom.gallery, so tiles
   are metafield files rather than product media: images swap the big view,
   videos play in place. Rendered twice — the mobile bottom sheet and the desktop
   in-place takeover — so this binds per [data-media-panel] rather than to a
   single sheet id. */
(() => {
  document.querySelectorAll("[data-media-panel]").forEach((panel) => {
    const view = panel.querySelector("[data-media-view]");
    const video = panel.querySelector("[data-media-video]");
    const strip = panel.querySelector("[data-media-strip]");
    const play = panel.querySelector("[data-media-play]");
    let currentIsVideo = false;
    if (!view || !strip) return;

    /* Big play button over the poster: it goes away once playback starts and
       returns when the clip ends. Controls arrive with the first play so the
       poster stays clean until then. */
    /* While a clip plays, the gallery's own furniture (toggle, dots) sits on top
       of it — get out of the way and come back on pause or when it ends. */
    const wrap = panel.closest(".pdp-gallery-wrap");
    const setPlaying = (on) =>
      wrap && wrap.classList.toggle("is-media-playing", on);

    if (play && video) {
      play.addEventListener("click", () => {
        video.controls = true;
        video.play();
      });
      video.addEventListener("play", () => {
        play.hidden = true;
        video.controls = true;
        setPlaying(true);
      });
      /* Pausing has to bring the button back, not just ending. Guarded on the
         current item because select() pauses the clip when you switch away, and
         the pause event lands after select() has already hidden the button —
         without the guard it reappeared over a still image. */
      video.addEventListener("pause", () => {
        setPlaying(false);
        if (currentIsVideo) play.hidden = false;
      });
      video.addEventListener("ended", () => {
        play.hidden = false;
        setPlaying(false);
      });
    }

    const tiles = () =>
      Array.from(strip.querySelectorAll(".pdp-media-sheet__tile"));
    const prev = panel.querySelector("[data-media-prev]");
    const next = panel.querySelector("[data-media-next]");

    const select = (idx) => {
      const tile = tiles()[idx];
      if (!tile) return;
      tiles().forEach((t, i) => t.classList.toggle("active", i === idx));
      /* no wrap-around, matching the gallery's arrows */
      if (prev) prev.disabled = idx === 0;
      if (next) next.disabled = idx === tiles().length - 1;

      const isVideo = tile.dataset.mediaType === "video";
      currentIsVideo = isVideo;
      if (video) {
        video.hidden = !isVideo;
        if (isVideo) {
          if (video.src !== tile.dataset.videoSrc) {
            video.src = tile.dataset.videoSrc;
            video.poster = tile.dataset.mediaSrc;
          }
        } else {
          video.pause();
        }
      }
      view.hidden = isVideo;
      if (!isVideo) view.src = tile.dataset.mediaSrc;
      /* a newly selected clip starts from its poster, so the button comes back */
      if (play) play.hidden = !isVideo;
      if (video && !isVideo) video.controls = false;
      setPlaying(false);
    };

    strip.addEventListener("click", (e) => {
      const tile = e.target.closest(".pdp-media-sheet__tile");
      if (tile) select(Number(tile.dataset.mediaIndex));
    });

    const step = (dir) => {
      const current = tiles().findIndex((t) => t.classList.contains("active"));
      select(current + dir);
    };
    if (prev) prev.addEventListener("click", () => step(-1));
    if (next) next.addEventListener("click", () => step(1));

    /* Swipe the media itself, not just the dots. Pointer events rather than a
       scroll-snap track: the panel shows one item at a time by design, so there
       is nothing to scroll — and a track would have to duplicate <pdp-gallery>.
       No wrap-around, matching the gallery's arrows. */
    let startX = 0;
    let startY = 0;
    let tracking = false;
    /* Bind to the container, not to [data-media-view]: that is the <img>, and
       dragging an image starts a native HTML5 drag, which fires pointercancel
       and swallows the pointerup the swipe depends on. The CSS kills the drag
       too, but the container is the right target regardless — it stays put when
       the img is hidden for a video. */
    const surface = panel.querySelector(".pdp-media-sheet__view") || view;
    surface.addEventListener("pointerdown", (e) => {
      /* never fight the native controls mid-playback: a drag on the seek
         bar is not a swipe */
      if (video && !video.paused) return;
      if (video && e.target === video && video.controls) return;
      tracking = true;
      startX = e.clientX;
      startY = e.clientY;
    });
    surface.addEventListener("pointerup", (e) => {
      if (!tracking) return;
      tracking = false;
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      /* horizontal, and decisively so — otherwise it was a tap or a page scroll */
      if (Math.abs(dx) < 40 || Math.abs(dx) <= Math.abs(dy)) return;
      step(dx < 0 ? 1 : -1);
    });
    surface.addEventListener("pointercancel", () => {
      tracking = false;
    });

    /* Trackpad swipes arrive as wheel events, not pointer events. One gesture
       (including its momentum tail) steps once: the lock only releases after
       200ms without wheel input. preventDefault also keeps macOS from reading
       the swipe as history back/forward. */
    let wheelLock = false;
    let wheelIdle;
    surface.addEventListener(
      "wheel",
      (e) => {
        if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
        e.preventDefault();
        clearTimeout(wheelIdle);
        wheelIdle = setTimeout(() => (wheelLock = false), 200);
        if (wheelLock || Math.abs(e.deltaX) < 10) return;
        wheelLock = true;
        step(e.deltaX > 0 ? 1 : -1);
      },
      { passive: false },
    );

    /* stop playback when this panel is hidden, or audio keeps running behind it */
    const host = panel.closest("nays-bottom-sheet, .pdp-media-view");
    if (host) {
      new MutationObserver(() => {
        const shown = host.classList
          ? host.classList.contains("active") || host.hasAttribute("open")
          : false;
        if (!shown && video) video.pause();
      }).observe(host, {
        attributes: true,
        attributeFilter: ["hidden", "open", "class"],
      });
    }

    select(0);
  });
})();

/* "Alle Details": popover on desktop (short content — a full drawer was
   overkill for ~4 lines), bottom sheet on mobile where a popover near the fold is
   awkward. Light-dismiss on outside click and Esc. */
(() => {
  const desktop = window.matchMedia(
    "(min-width: 1025px), (min-width: 769px) and (orientation: landscape)",
  );
  const closeAll = () => {
    document
      .querySelectorAll("[data-specs-pop]")
      .forEach((p) => (p.hidden = true));
    document
      .querySelectorAll("[data-specs-link]")
      .forEach((l) => l.setAttribute("aria-expanded", "false"));
  };

  document.addEventListener("click", (e) => {
    const link = e.target.closest && e.target.closest("[data-specs-link]");
    if (!link) {
      if (!e.target.closest || !e.target.closest("[data-specs-pop]"))
        closeAll();
      return;
    }
    if (!desktop.matches) {
      const sheet = document.getElementById("pdp-selection-sheet");
      if (sheet && typeof sheet.open === "function") sheet.open();
      return;
    }
    const pop = link.parentElement.querySelector("[data-specs-pop]");
    if (!pop) return;
    const wasOpen = !pop.hidden;
    closeAll();
    pop.hidden = wasOpen;
    link.setAttribute("aria-expanded", String(!wasOpen));
  });

  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") closeAll();
  });
})();

/* Per-variant specs: every variant with specs is rendered hidden in the sheet,
   so switching variant is a class toggle rather than a request. The link hides
   entirely for variants that have none. */
document.addEventListener("change", (e) => {
  if (!e.target.closest || !e.target.closest("product-variants")) return;
  /* the theme updates currentVariant asynchronously */
  setTimeout(() => {
    const pv = document.querySelector("product-variants");
    const variant = pv && pv.currentVariant;
    /* No variant means the chosen option combination does not exist. Treat that
       as "no specs", not "no change" — returning early here left the previous
       variant's link on screen, naming something that cannot be bought
       (2.0 INCH + C-12 showed "Alle Details zu C-12" under "Nicht verfügbar"). */
    const panes = document.querySelectorAll("[data-specs-for]");
    let variantMatch = false;
    panes.forEach((pane) => {
      if (pane.dataset.specsFor === "product") return;
      const match = !!variant && pane.dataset.specsFor === String(variant.id);
      pane.hidden = !match;
      if (match) variantMatch = true;
    });
    /* Product-level specs are the fallback, not an addition: shown only when the
       selected variant has none of its own (variant wins). Default-variant
       products have nothing else, which is why this exists.
       Deliberately still shown when `variant` is null (an option combination
       that does not exist): these specs describe the product, so nothing on
       screen names something unbuyable — the reason the variant-level link is
       hidden in that case. */
    const productPane = document.querySelector('[data-specs-for="product"]');
    if (productPane) productPane.hidden = variantMatch;
    const has = variantMatch || !!productPane;
    /* An open panel must not survive the switch: its pane is now hidden, so it
       would sit there as an empty box. */
    document.querySelectorAll("[data-specs-pop]").forEach((pop) => {
      pop.hidden = true;
    });
    document.querySelectorAll("[data-specs-link]").forEach((link) => {
      link.hidden = !has;
      link.setAttribute("aria-expanded", "false");
      /* The label itself changes with the source, not just the name after it:
         "Alle Details zu" only reads correctly with a variant name following,
         so the product-level fallback uses its own string. Both come from
         data-* on the button, set from locale keys in the snippet. */
      const text = link.querySelector("[data-specs-link-label]");
      /* The trailing name belongs to variant-level specs only. On the
         product-level fallback it would name an arbitrary option that the specs
         do not describe, so it is emptied and hidden instead. */
      const label = link.querySelector("[data-specs-link-variant]");
      if (!label) return;
      if (variantMatch && variant && variant.options) {
        if (text && link.dataset.labelVariant)
          text.textContent = link.dataset.labelVariant;
        /* separator lives with the name, not in the locale string — see the
           snippet: otherwise the product-level case shows a trailing colon */
        label.textContent = " " + variant.options[variant.options.length - 1];
        label.hidden = false;
      } else {
        if (text && link.dataset.labelProduct)
          text.textContent = link.dataset.labelProduct;
        label.textContent = "";
        label.hidden = true;
      }
    });
  }, 100);
});

/* T48: free-shipping label — "zzgl. Versandkosten" <-> "inkl. Versand" when
   the selected variant crosses the market threshold (data-fs-threshold in
   cents, from market.custom.free_shipping_threshold; server renders the
   initial state). */
(() => {
  const holder = document.querySelector(
    ".nays-pdp-2026 [data-product-policies][data-fs-threshold]",
  );
  if (!holder) return;
  const threshold = parseInt(holder.dataset.fsThreshold, 10);
  const zzgl = holder.querySelector("[data-fs-zzgl]");
  const inkl = holder.querySelector("[data-fs-inkl]");
  const idInput = document.querySelector(
    ".nays-pdp-2026 product-form input[name='id']",
  );
  const blob = document.getElementById("variants-json");
  if (!threshold || !zzgl || !inkl || !idInput || !blob) return;
  let variants = [];
  try {
    variants = JSON.parse(blob.textContent);
  } catch (err) {
    return; /* malformed payload — keep server-rendered state */
  }
  const apply = () => {
    const v = variants.find((x) => String(x.id) === String(idInput.value));
    if (!v) return;
    const free = v.price >= threshold;
    zzgl.hidden = free;
    inkl.hidden = !free;
  };
  document.addEventListener("change", (e) => {
    if (e.target.closest && e.target.closest("product-variants")) {
      setTimeout(apply, 0); // theme JS writes the hidden id input first
    }
  });
})();

/* NAYS-1058: "Halte mich informiert" on pre-release products. The dialog holds a
   native customer form, so a submit reloads the page; its action ends in
   #pdp-notify-form, which is how we know to reopen it with the confirmation. */
(() => {
  const dialog = document.getElementById("pdp-notify");
  const open = document.querySelector("[data-pdp-notify-open]");
  if (!dialog || !open) return;

  open.addEventListener("click", () => dialog.showModal());
  dialog
    .querySelector("[data-pdp-notify-close]")
    .addEventListener("click", () => dialog.close());
  /* a click on the ::backdrop targets the dialog itself; the panel covers the
     rest, so this only fires outside it */
  dialog.addEventListener("click", (e) => {
    if (e.target === dialog) dialog.close();
  });

  if (location.hash === "#pdp-notify-form") {
    dialog.showModal();
    history.replaceState(null, "", location.pathname + location.search);
  }

  /* variant-level pre-release: follow the selection. VARIANT_CHANGE does not
     bubble, so bind to <product-variants> directly (as the gallery does). */
  const variants = open
    .closest("product-page")
    ?.querySelector("product-variants");
  if (variants) {
    variants.addEventListener("VARIANT_CHANGE", (e) => {
      open.hidden = !(
        variants.dataset.isProductPrerelease === "true" ||
        e.target.currentVariant?.prerelease
      );
    });
  }
})();

/* NAYS-1064: uploaded feature icons (custom_icon) are Lucide SVGs drawn with
   stroke="currentColor". As an <img> that resolves to black whatever the page
   colour is, so browser dark modes that recolour text (Dark Reader and co.)
   left them black on dark. Inlined, they inherit the text colour like the
   built-in icons. Same-origin files; anything carrying script or handlers
   stays an <img>. */
document
  .querySelectorAll(".nays-pdp-2026 .pdp-feature__icon-holder img[src*='.svg']")
  .forEach(async (img) => {
    try {
      const res = await fetch(img.src);
      if (!res.ok) return;
      const svg = new DOMParser().parseFromString(
        await res.text(),
        "image/svg+xml",
      ).documentElement;
      const unsafe =
        svg.nodeName !== "svg" ||
        svg.querySelector("script, foreignObject") ||
        [svg, ...svg.querySelectorAll("*")].some((n) =>
          [...n.attributes].some(
            (a) => /^on/i.test(a.name) || /javascript:/i.test(a.value),
          ),
        );
      if (unsafe) return;
      svg.setAttribute("class", "pdp-feature__icon");
      svg.setAttribute("aria-hidden", "true");
      svg.setAttribute("focusable", "false");
      img.replaceWith(document.importNode(svg, true));
    } catch (e) {
      /* leave the <img> — it still renders, just not dark-mode aware */
    }
  });
